// Abono a capital desde el expediente: quién lo ve, el rango del monto y qué decir de cada rechazo
// del backend. Puro, sin React.
import { LoanCreditStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanCreditStatusEnum';
import { ApiError, userMessageOf } from '@/api/errors';
import type { LoanPrepaymentQuote } from '@/api/services/loan-credit';
import { formatMoneyCents } from './logic';

export const CREDIT_PREPAY_PERMISSION = 'loan.credit.prepay';

/** Solo un crédito activo abona a capital; el backend rechaza además el que tiene cuotas vencidas. */
export function canOfferPrepayment(status: LoanCreditStatusEnum): boolean {
    return status === LoanCreditStatusEnum.ACTIVE;
}

export interface PrepaymentRange {
    minCents: number;
    /** El máximo que publica la cotización: lo menor entre la liquidación y el capital que se re-amortiza, menos un centavo. */
    maxCents: number;
}

export function prepaymentRangeOf(quote: LoanPrepaymentQuote): PrepaymentRange {
    return { minCents: quote.minAmountCents, maxCents: quote.maxAmountCents };
}

/** El mínimo del plan no deja espacio antes de liquidar: no hay abono posible. */
export function isPrepaymentAvailable(range: PrepaymentRange): boolean {
    return range.minCents <= range.maxCents;
}

/** Pesos tecleados → centavos. Vacío o ilegible es `null`. */
export function typedPesosToCents(text: string): number | null {
    const value = Number(text);
    if (text.trim() === '' || !Number.isFinite(value)) return null;
    return Math.round(value * 100);
}

/** Qué le falta al monto capturado; `null` cuando está dentro del rango. */
export function prepaymentAmountError(
    amountCents: number | null,
    range: PrepaymentRange,
): string | null {
    if (amountCents === null) return 'Captura el monto del abono.';
    if (amountCents < range.minCents) {
        return `El abono mínimo es ${formatMoneyCents(range.minCents)}.`;
    }
    if (amountCents > range.maxCents) {
        return `El abono máximo es ${formatMoneyCents(range.maxCents)}.`;
    }
    return null;
}

/** Un monto en cents que vino en el `data` del rechazo, ya formateado. */
function centsFrom(data: unknown, key: string): string | null {
    if (typeof data !== 'object' || data === null) return null;
    const value = (data as Record<string, unknown>)[key];
    return typeof value === 'number' ? formatMoneyCents(value) : null;
}

/**
 * Qué decirle al operador por cada rechazo. `quote` y `apply` comparten códigos pero no causa: en
 * la cotización, `CREDIT_NOT_CURRENT` es un crédito que todavía no se activa.
 */
export function prepaymentErrorMessage(error: unknown, stage: 'quote' | 'apply'): string {
    if (!(error instanceof ApiError)) return userMessageOf(error);
    switch (error.code) {
        case 'PREPAYMENT_BELOW_PERIOD_PAYMENT': {
            const min = centsFrom(error.data, 'minAmountCents');
            return min
                ? `El abono es menor al mínimo del plan: captura al menos ${min}.`
                : 'El abono es menor al mínimo del plan.';
        }
        case 'PREPAYMENT_COVERS_SETTLEMENT_AMOUNT': {
            const max = centsFrom(error.data, 'maxAmountCents');
            return `Con ese monto el crédito se liquida${max ? `: el abono puede ser de hasta ${max}` : ''}. Para cubrirlo todo, ofrece la liquidación anticipada.`;
        }
        case 'AD_HOC_CHARGE_PENDING':
            return 'Ya hay un cobro fuera de calendario en curso para este crédito. Espera su resultado antes de pedir otro.';
        case 'CREDIT_NOT_CURRENT':
            return stage === 'quote'
                ? 'El crédito todavía no se activa: no tiene saldo al que abonar.'
                : 'El crédito no está al corriente: el cliente primero debe pagar lo vencido.';
        case 'CREDIT_PLAN_BATCHES_PENDING':
            return 'La tabla del crédito todavía se está escribiendo. Intenta en unos minutos.';
        case 'COLLECTION_ENGINE_UNAVAILABLE':
            return 'El motor de cobro no respondió y el cobro pudo quedar en curso. Revisa los pagos del expediente antes de reintentar.';
        case 'CREDIT_NOT_FOUND':
            return 'No encontramos este crédito.';
        default:
            return userMessageOf(error);
    }
}
